/**
 * The first tab of a policy: the settings that govern it on the left, and on
 * the right the facts the header strip does not carry — publication, what
 * it covers, who has read it, where it came from.
 *
 * The right column is computed from the same snapshots the other tabs show,
 * so a count here is always a promise the tab behind it keeps.
 *
 * @module components/regulation/policy/PolicyOverviewTab
 */
import {
  FileTextIcon,
  Link2Icon,
  MessageSquareIcon,
  ShieldCheckIcon,
} from "lucide-react";
import type { ComponentType, ReactNode } from "react";

import type { Policy } from "../../../state/policies";
import type { PolicyGovernance } from "../../../state/governance";
import { PolicySettingsTab, formatDate } from "./PolicyGovernanceTabs";

export interface PolicyOverviewCounts {
  readonly controls: number | null;
  readonly versions: number | null;
  readonly comments: number | null;
}

function Fact({
  icon: Icon,
  label,
  children,
}: {
  readonly icon: ComponentType<{ className?: string }>;
  readonly label: string;
  readonly children: ReactNode;
}) {
  return (
    <div className="flex items-start gap-3 py-2.5">
      <span className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
        <Icon className="size-3.5" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-xs text-muted-foreground">
          {label}
        </p>
        <div className="mt-0.5 text-sm text-foreground">{children}</div>
      </div>
    </div>
  );
}

/** A count that has not loaded is a dash, never a zero. */
const countOr = (value: number | null, singular: string, plural = `${singular}s`): string =>
  value === null ? "—" : `${value} ${value === 1 ? singular : plural}`;

export function PolicyOverviewTab({
  organizationId,
  policyId,
  policy,
  governance,
  counts,
}: {
  readonly organizationId: string | null;
  readonly policyId: string;
  readonly policy: Policy | null;
  readonly governance: PolicyGovernance | null;
  readonly counts: PolicyOverviewCounts;
}) {
  return (
    // Sized by the page's `policy` container, not the window: side by side
    // only when the settings keep room for two columns of fields. Stacked,
    // the facts card runs its three facts across instead of down.
    <div className="grid grid-cols-1 items-start gap-4 @[60rem]/policy:grid-cols-[minmax(0,1fr)_340px]">
      <PolicySettingsTab organizationId={organizationId} policyId={policyId} />

      <aside className="rounded-xl border border-border/60 bg-card px-5 py-3">
        <h2 className="py-2 text-sm font-semibold text-foreground">At a glance</h2>
        <div className="divide-y divide-border/60 @[40rem]/policy:@max-[60rem]/policy:grid @[40rem]/policy:@max-[60rem]/policy:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1.5fr)] @[40rem]/policy:@max-[60rem]/policy:gap-x-6 @[40rem]/policy:@max-[60rem]/policy:divide-y-0">
          <Fact icon={Link2Icon} label="Coverage">
            {counts.controls === 0 ? (
              <span className="text-muted-foreground">No controls linked yet</span>
            ) : (
              <>
                {countOr(counts.controls, "control")}
                <span className="text-muted-foreground"> linked</span>
              </>
            )}
          </Fact>
          <Fact icon={MessageSquareIcon} label="History">
            {counts.versions === 0 && counts.comments === 0 ? (
              <span className="text-muted-foreground">No versions or comments yet</span>
            ) : (
              <>
                {countOr(counts.versions, "version")}
                <span className="text-muted-foreground"> · </span>
                {countOr(counts.comments, "comment")}
              </>
            )}
          </Fact>
          <Fact icon={FileTextIcon} label="Source">
            <span className="[overflow-wrap:anywhere]">{policy?.filename ?? "—"}</span>
            {policy?.uploadedByEmail ? (
              <span className="text-muted-foreground"> · by {policy.uploadedByEmail}</span>
            ) : null}
            {policy?.template ? (
              <span className="block text-muted-foreground">
                Drafted from the template “{policy.template.title}” (v{policy.template.version})
              </span>
            ) : null}
          </Fact>
        </div>
      </aside>
    </div>
  );
}
